import { Button, buttonClasses, cn, PageHeader } from '@fruuit/ui'
import { ArrowLeft, ArrowRight, Compass, LifeBuoy, Printer, SearchX } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router'
import type { AreaId } from '../../api/types'
import { AREAS } from '../../areas/areas'
import { useSession } from '../../auth/session-context'
import { AREA_SECTION, findHelpSection, HELP_SECTIONS } from '../../features/help/content'
import { HelpBlockView, isWide } from '../../features/help/HelpBlocks'
import { HelpMobileNav, HelpToc, sectionPath } from '../../features/help/HelpToc'
import { filterHelp } from '../../features/help/search'
import type { HelpSection } from '../../features/help/types'
import { useTitle } from '../../lib/useTitle'
import { currentArea } from '../../shell/lastArea'
import '../../features/help/help.css'

const AUDIENCE_LABEL: Record<AreaId, string> = {
  app: 'investors',
  workspace: 'the Fruuit team',
  admin: 'administrators',
}

/** Scroll a heading into view once the shell's own route effects have run. */
function scrollToHash(hash: string) {
  const id = decodeURIComponent(hash.replace(/^#/, ''))
  if (!id) return
  requestAnimationFrame(() => {
    const target = document.getElementById(id)
    if (!target) return
    target.scrollIntoView?.({ block: 'start' })
    target.focus?.({ preventScroll: true })
  })
}

/** The two-column frame shared by the home page and the section pages. */
function HelpFrame({
  current,
  children,
}: {
  current: HelpSection | null
  children: React.ReactNode
}) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const matches = useMemo(() => filterHelp(HELP_SECTIONS, query), [query])
  const jump = useCallback((path: string) => navigate(path), [navigate])

  return (
    <div className="help-grid mt-2 grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-10">
      <aside className="hidden lg:block print:hidden">
        <div className="sticky top-6 max-h-[calc(100dvh-12rem)] overflow-y-auto pb-4">
          <HelpToc
            matches={matches}
            current={current}
            query={query}
            onQuery={setQuery}
            total={HELP_SECTIONS.length}
          />
        </div>
      </aside>
      <div className="flex min-w-0 flex-col gap-6">
        <div className="lg:hidden print:hidden">
          <HelpMobileNav
            matches={matches}
            current={current}
            query={query}
            onQuery={setQuery}
            onJump={jump}
          />
        </div>
        {query && matches.length === 0 ? (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-line-strong bg-surface/70 px-6 py-12 text-center">
            <SearchX aria-hidden="true" className="mb-3 size-6 text-teal-500" />
            <p className="font-display text-base font-semibold text-ink">Nothing matches</p>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-sage">
              No heading or text in Help mentions “{query.trim()}”. Try a shorter word, a screen
              name or a label as it appears on screen.
            </p>
            <Button variant="secondary" className="mt-5" onClick={() => setQuery('')}>
              Show all sections
            </Button>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  )
}

function PrintButton() {
  return (
    <Button
      variant="secondary"
      icon={<Printer />}
      onClick={() => window.print()}
      className="print:hidden"
    >
      Print
    </Button>
  )
}

function SectionBody({ section }: { section: HelpSection }) {
  return (
    <div className="flex flex-col gap-4 text-[15px] leading-[1.7] text-ink-soft">
      {section.blocks.map((block, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: blocks are static, ordered content
          key={index}
          className={isWide(block) ? 'min-w-0' : 'min-w-0 max-w-[72ch]'}
        >
          <HelpBlockView block={block} />
        </div>
      ))}
    </div>
  )
}

function SectionCard({ section, children }: { section: HelpSection; children: React.ReactNode }) {
  return (
    <article
      aria-labelledby={`${section.id}-title`}
      className="help-article rounded-lg border border-line bg-surface px-5 py-6 shadow-[0_1px_2px_rgb(22_62_58/0.04)] sm:px-8 sm:py-8"
    >
      {children}
    </article>
  )
}

/** The "Start here" card: the section most relevant to the area the reader came from. */
function StartHere({ area }: { area: AreaId }) {
  const section = findHelpSection(AREA_SECTION[area])
  if (!section) return null
  const Icon = section.icon
  return (
    <Link
      to={sectionPath(section)}
      data-testid="help-start-here"
      className="group flex items-start gap-4 rounded-lg border border-[#f0d3b5] bg-[#fdf3e7] p-5 no-underline transition-colors hover:border-[#e9b97f] sm:p-6"
    >
      <span
        aria-hidden="true"
        className="grid size-11 shrink-0 place-items-center rounded-md border border-[#f0d3b5] bg-surface text-[#b8652a] [&>svg]:size-5"
      >
        <Icon />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-semibold uppercase tracking-[var(--tracking-kicker)] text-[#b8652a]">
          Start here · {AREAS[area].label} area
        </span>
        <span className="mt-1 block font-display text-lg font-semibold text-ink">
          {section.title}
        </span>
        <span className="mt-1 block text-sm leading-relaxed text-ink-soft">{section.summary}</span>
      </span>
      <ArrowRight
        aria-hidden="true"
        className="mt-1 size-5 shrink-0 text-[#b8652a] transition-transform group-hover:translate-x-0.5"
      />
    </Link>
  )
}

function SectionGrid({ exclude }: { exclude: string }) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2" aria-label="All help sections">
      {HELP_SECTIONS.filter((section) => section.id !== exclude).map((section) => {
        const Icon = section.icon
        return (
          <li key={section.id}>
            <Link
              to={sectionPath(section)}
              className="group flex h-full items-start gap-3 rounded-lg border border-line bg-surface p-4 no-underline transition-colors hover:border-teal-300 hover:bg-teal-50/40"
            >
              <Icon
                aria-hidden="true"
                className="mt-0.5 size-[18px] shrink-0 text-teal-500 group-hover:text-teal"
              />
              <span className="min-w-0">
                <span className="block font-display text-[15px] font-semibold text-ink">
                  {section.title}
                </span>
                <span className="mt-0.5 block text-[13px] leading-relaxed text-sage">
                  {section.summary}
                </span>
              </span>
            </Link>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * /help: the Help centre home. A "Start here" card for the reader's area, the welcome
 * section, and the list of every section.
 */
export function HelpHomePage() {
  useTitle('Help')
  const me = useSession()
  const area = currentArea(me) ?? 'app'
  const welcome = findHelpSection('welcome')
  if (!welcome) return null
  return (
    <div data-help-root>
      <PageHeader
        eyebrow="Help centre"
        title="How can we help?"
        description={`Plain-English guides to Fruuit Engage for ${AUDIENCE_LABEL[area]}, the Fruuit team and administrators. Everyone can read every section.`}
        actions={<PrintButton />}
      />
      <HelpFrame current={welcome}>
        <StartHere area={area} />
        <SectionCard section={welcome}>
          <header className="mb-5">
            <h2
              id={`${welcome.id}-title`}
              className="font-display text-[22px] font-semibold leading-tight tracking-[var(--tracking-heading)] text-ink sm:text-2xl"
            >
              {welcome.title}
            </h2>
          </header>
          <SectionBody section={welcome} />
        </SectionCard>
        <section aria-labelledby="help-all-title" className="print:hidden">
          <h2
            id="help-all-title"
            className="mb-3 font-display text-[17px] font-semibold tracking-[-0.01em] text-ink"
          >
            All sections
          </h2>
          <SectionGrid exclude="welcome" />
        </section>
      </HelpFrame>
    </div>
  )
}

function NotAvailable() {
  useTitle('Not available')
  return (
    <div className="flex flex-col items-center rounded-lg border border-dashed border-line-strong bg-surface/70 px-6 py-14 text-center">
      <span
        aria-hidden="true"
        className="mb-4 flex size-12 items-center justify-center rounded-full bg-wash text-teal-500 ring-1 ring-line [&>svg]:size-[22px]"
      >
        <Compass />
      </span>
      <h1 className="font-display text-[22px] font-semibold tracking-[var(--tracking-heading)] text-ink">
        This help page is not available
      </h1>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-sage">
        The address may be mistyped. The Help centre lists every section.
      </p>
      <Link to="/help" className={cn('mt-5', buttonClasses({ variant: 'primary' }))}>
        Open the Help centre
      </Link>
    </div>
  )
}

/** /help/:section: one section, with its neighbours linked at the bottom. */
export function HelpSectionPage() {
  const { section: id = '' } = useParams()
  const section = findHelpSection(id)
  if (id === 'welcome') return <Navigate to="/help" replace />
  if (!section) return <NotAvailable />
  return <HelpSectionView section={section} />
}

function HelpSectionView({ section }: { section: HelpSection }) {
  useTitle(section.title, 'Help')
  const { hash } = useLocation()
  const Icon = section.icon
  const index = HELP_SECTIONS.indexOf(section)
  const previous = HELP_SECTIONS[index - 1]
  const next = HELP_SECTIONS[index + 1]

  // Deep links: /help/investors#reports-and-exports on load and on back/forward.
  useEffect(() => {
    scrollToHash(hash)
  }, [hash])

  return (
    <div data-help-root>
      <PageHeader
        eyebrow={
          section.audience === 'all'
            ? 'Help centre'
            : `Help centre · For ${AUDIENCE_LABEL[section.audience]}`
        }
        title={section.title}
        description={section.summary}
        actions={<PrintButton />}
      />
      <HelpFrame current={section}>
        <SectionCard section={section}>
          <header className="mb-6 flex items-start gap-4">
            <span
              aria-hidden="true"
              className="hidden size-11 shrink-0 place-items-center rounded-md border border-[#f0d3b5] bg-[#fdf3e7] text-[#b8652a] sm:grid [&>svg]:size-5"
            >
              <Icon />
            </span>
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-teal-500">
                <LifeBuoy aria-hidden="true" className="size-3.5" />
                Section {index + 1} of {HELP_SECTIONS.length}
              </p>
              <h2
                id={`${section.id}-title`}
                tabIndex={-1}
                className="mt-0.5 font-display text-[22px] font-semibold leading-tight tracking-[var(--tracking-heading)] text-ink focus:outline-none sm:text-2xl"
              >
                {section.title}
              </h2>
            </div>
          </header>
          <SectionBody section={section} />
          <nav
            aria-label="Previous and next section"
            className="mt-8 flex flex-col gap-3 border-t border-line-soft pt-5 sm:flex-row sm:justify-between print:hidden"
          >
            {previous ? (
              <Link
                to={sectionPath(previous)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal no-underline hover:underline"
              >
                <ArrowLeft aria-hidden="true" className="size-4" />
                {previous.title}
              </Link>
            ) : (
              <span />
            )}
            {next ? (
              <Link
                to={sectionPath(next)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal no-underline hover:underline sm:text-right"
              >
                {next.title}
                <ArrowRight aria-hidden="true" className="size-4" />
              </Link>
            ) : null}
          </nav>
        </SectionCard>
      </HelpFrame>
    </div>
  )
}
